import React from 'react';
import Modal from '../../../../components/Modal';
import DropDown from '../../../../components/DropDown';
import InputField from '../../../../components/InputField';
import { useEditDetails } from './EditDetailsData';
import { ENTITY_LOB_TYPE_OPTIONS, LOB_NATIONALITY_OPTIONS } from '../../../../constants/formOptions';
import './index.scss';

const ENTITY_INFO_FIELDS = [
    { name: 'entityId', label: 'Entity ID', disabled: true },
    { name: 'entityCategory', label: 'Entity Category', disabled: true },
    { name: 'entityName', label: 'Entity Name', required: true, fullWidth: true },
    { name: 'entityAddress', label: 'Entity Address', type: 'textarea', rows: 2, required: true, fullWidth: true },
    { name: 'pinCode', label: 'Pin Code', maxLength: 6 },
    { name: 'city', label: 'City', disabled: true },
    { name: 'state', label: 'State', disabled: true },
];

const BUSINESS_FIELDS = [
    { name: 'natureOfBusiness', label: 'Nature of Business' },
    { name: 'panNumber', label: 'Individual PAN Number', maxLength: 20 },
    { name: 'companyPAN', label: 'Company PAN Number', maxLength: 20 },
    { name: 'isGSTRegistered', label: 'GST Registered', type: 'checkbox' },
    { name: 'isRegisteredUnderMSME', label: 'MSME Registered', type: 'checkbox' },
];

const CONTACT_FIELDS = [
    { name: 'contactPerson', label: 'Contact Person', required: true },
    { name: 'designation', label: 'Designation' },
    { name: 'mobileNumber', label: 'Mobile Number', type: 'tel', maxLength: 10, required: true },
    { name: 'emailAddress', label: 'Email Address', type: 'email', required: true },
];

const BANK_FIELDS = [
    { name: 'bankName', label: 'Bank Name' },
    { name: 'branchNameAndAddress', label: 'Branch Name & Address' },
    { name: 'accountNumber', label: 'Account Number' },
    { name: 'ifscCode', label: 'IFSC Code', maxLength: 11 },
    { name: 'beneficiaryName', label: 'Beneficiary Name' },
    { name: 'thirdPartyPayment', label: 'Third Party Payment', type: 'checkbox' },
];

const renderFields = (fields, entity, handleChange) =>
    fields.map(({ name, ...props }) => (
        <InputField
            key={name}
            name={name}
            value={props.type === 'checkbox' ? (entity[name] || false) : (entity[name] || '')}
            onChange={handleChange}
            {...props}
        />
    ));

const EditClientModal = ({ isOpen, onClose, clientData, onSave }) => {
    const { formData: entity, isSubmitting, departmentOptions, handleChange, handleSubmit, handleClose, getDocumentStatusBadge } =
        useEditDetails(clientData, onSave, onClose);

    if (!clientData) return null;
    const documents = clientData.documents || [];

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Edit Entity" size="large">
            <form onSubmit={handleSubmit} className="edit-entity-form">
                <div className="form-content">

                    {/* Entity Information */}
                    <div className="form-section">
                        <h3 className="section-title">Entity Information</h3>
                        <div className="form-grid">
                            {renderFields(ENTITY_INFO_FIELDS, entity, handleChange)}
                            <div className="form-group">
                                <DropDown name="lobType" value={entity.lobType || ''} onChange={handleChange}
                                    label="LOB Type" placeholder="Select LOB Type" options={ENTITY_LOB_TYPE_OPTIONS} fullWidth />
                            </div>
                            <div className="form-group">
                                <DropDown name="lobNationality" value={entity.lobNationality || ''} onChange={handleChange}
                                    label="LOB Nationality" placeholder="Select Nationality" options={LOB_NATIONALITY_OPTIONS} fullWidth />
                            </div>
                        </div>
                    </div>

                    {/* Business Details */}
                    <div className="form-section">
                        <h3 className="section-title">Business Details</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <DropDown
                                    name="aaDepartmentFunction"
                                    value={entity.aaDepartmentFunction || ''}
                                    onChange={handleChange}
                                    label="AA Department/Function"
                                    options={departmentOptions}
                                    fullWidth
                                />
                            </div>
                            {renderFields(BUSINESS_FIELDS, entity, handleChange)}
                            {entity.isGSTRegistered && (
                                <>
                                    <InputField name="gstNumber" label="GST Number" maxLength={15}
                                        value={entity.gstNumber || ''} onChange={handleChange} />
                                    <InputField name="gstAddress" label="GST Address"
                                        value={entity.gstAddress || ''} onChange={handleChange} />
                                    <InputField name="otherGSTNumbers" label="Other GST Numbers"
                                        value={entity.otherGSTNumbers || ''} onChange={handleChange} />
                                </>
                            )}
                        </div>
                    </div>

                    {/* Contact Details */}
                    <div className="form-section">
                        <h3 className="section-title">Contact Details</h3>
                        <div className="form-grid">
                            {renderFields(CONTACT_FIELDS, entity, handleChange)}
                        </div>
                    </div>

                    {/* Bank Details */}
                    <div className="form-section">
                        <h3 className="section-title">Bank Details</h3>
                        <div className="form-grid">
                            {renderFields(BANK_FIELDS, entity, handleChange)}
                            {entity.thirdPartyPayment && (
                                <InputField name="relationWithBeneficiary" label="Relation with Beneficiary"
                                    value={entity.relationWithBeneficiary || ''} onChange={handleChange} fullWidth />
                            )}
                        </div>
                    </div>

                    {/* Documents — read only */}
                    <div className="form-section">
                        <h3 className="section-title">Documents ({documents.length})</h3>
                        {documents.length === 0 ? (
                            <p className="no-documents">No documents available</p>
                        ) : (
                            <div className="documents-list">
                                {documents.map((doc, index) => {
                                    const uploadedFiles = doc.files?.filter(f => f.isUploaded) || [];
                                    const hasFiles = uploadedFiles.length > 0;
                                    return (
                                        <div key={index} className="document-item">
                                            <div className="doc-info">
                                                <div className="doc-name">
                                                    {doc.documentName}
                                                    {doc.isMandatory && <span className="mandatory-badge">Required</span>}
                                                </div>
                                                {hasFiles && (
                                                    <div className="doc-files">
                                                        {uploadedFiles.map((file, i) => (
                                                            <div key={i} className="doc-filename">{file.fileName}</div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="doc-status-wrapper">
                                                {getDocumentStatusBadge(hasFiles)}
                                                {hasFiles && (
                                                    <div className="view-buttons">
                                                        {uploadedFiles.map((file, i) => (
                                                            <a key={i} href={file.filePath} target="_blank"
                                                                rel="noopener noreferrer" className="view-doc-btn">
                                                                View {uploadedFiles.length > 1 ? `(${i + 1})` : ''}
                                                            </a>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" className="btn-cancel" onClick={handleClose} disabled={isSubmitting}>
                        Cancel
                    </button>
                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default EditClientModal;
